import { Box, CircularProgress, Link as MuiLink, useMediaQuery } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { DuplicatePartsFragment } from './DuplicatePartsFragment';
import { MainPartEditFragment } from './MainPartEditFragment/MainPartEditFragment';
import { MainPartEditFragmentHandle } from './MainPartEditFragment/MainPartEditFragment.props';
import { LinkedJobsPanel } from './panels/LinkedJobsPanel';
import { LinkedShipmentsPanel } from './panels/LinkedShipmentsPanel';
import { PartHistoryFragment } from './PartHistoryFragment';
import { WarrantySummaryFragment } from './WarrantySummaryFragment';
import { ActionPermissions, ScreenSize } from '../../../@types';
import { PrintLabelEntityTypes } from '../../../@types/print-label.type';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { Panel } from '../../../components/atoms/Panel';
import { CreateRepairJobButton } from '../../../components/molecules/CreateRepairJobButton';
import { PartChildrenList } from '../../../components/molecules/PartChildrenList';
import { PartSummaryBar } from '../../../components/molecules/PartSummaryBar';
import { PrintLabel } from '../../../components/molecules/PintLabel';
import { PLATFORM_NAME } from '../../../configs/common';
import { isUserHasPermission } from '../../../configs/permissions';
import { TabHandler } from '../../../handlers/TabHandler';
import {
  useGetAllWarrantySummary,
  useGetDuplicatePartsBySerial1AndPartType,
  useGetOnePart,
  useGetPartConditions,
  useGetPartHistoryByPartId,
} from '../../../queries/part-query';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { toast } from '../../../utils/toast';
import { NotFoundError } from '../../Error/NotFound';

export const UpdatePart: React.FC = () => {
  const { id } = useParams();
  const partId = id ? parseInt(id) : -1;
  const isMobileView = useMediaQuery(ScreenSize.MOBILE);

  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const editRef = useRef<MainPartEditFragmentHandle>(null);

  const getOnePart = useGetOnePart(partId);
  const getPartConditions = useGetPartConditions();
  const getAllWarrantySummary = useGetAllWarrantySummary(partId);
  const getPartHistoryByPartId = useGetPartHistoryByPartId(partId);
  const permissionsQuery = useGetUserSystemPermissions();

  const part = getOnePart.data;
  const conditions = getPartConditions.data ?? [];
  const warrantySummary = getAllWarrantySummary.data ?? [];
  const partHistory = useMemo(
    () => getPartHistoryByPartId.data ?? [],
    [getPartHistoryByPartId.data]
  );

  const duplicatesQuery = useGetDuplicatePartsBySerial1AndPartType(
    part?.id ?? 0,
    part?.serial1 ?? '',
    part?.partTypeId ?? 0
  );

  TabHandler(part ? `Part: ${part.serial1} | Parts | ${PLATFORM_NAME}` : `Part | ${PLATFORM_NAME}`);

  const canEdit =
    isUserHasPermission(ActionPermissions.Part_Serial, permissionsQuery.data) ||
    isUserHasPermission(ActionPermissions.Part_Condition, permissionsQuery.data) ||
    isUserHasPermission(ActionPermissions.Structure_Edit, permissionsQuery.data);

  const canSeeMerge = isUserHasPermission(ActionPermissions.Parts_Merge, permissionsQuery.data);

  const handleDiscard = useCallback(() => {
    editRef.current?.reset();
  }, []);
  const handleDirtyChange = useCallback((dirty: boolean) => setIsDirty(dirty), []);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  if (!part) {
    return getOnePart.isFetching ? (
      <PageContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      </PageContainer>
    ) : (
      <NotFoundError />
    );
  }

  const partTypeName = part.partType?.name ?? '';
  const partNumber = part.partType?.number ?? part.partType?.partTypeNumber ?? '';
  const locationHierarchy = part.locationHierarchy || part.locationName || part.nodeName || '';

  const handleSave = async () => {
    if (!editRef.current) return;
    setSaving(true);
    try {
      await editRef.current.save();
    } catch (err) {
      // useUpdatePart and useAddTags don't define onError handlers (verified in
      // part-query.ts and tags-query.ts), so a failed mutation surfaces here as
      // a rejected promise. Show the user a toast so the click doesn't appear
      // to silently fail.
      const msg = err instanceof Error && err.message ? err.message : 'Failed to save part';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };
  const handleSaved = () => toast.success('Part updated');

  const duplicates = duplicatesQuery.data ?? [];
  const duplicateCount = duplicates.length;
  const showDuplicates = canSeeMerge && duplicateCount > 0 && !isMobileView;
  const duplicatesSlot = showDuplicates ? (
    <DuplicatePartsFragment duplicatePartsData={duplicates} mainPartId={part.id} />
  ) : undefined;

  return (
    <PageContainer>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 2 }}>
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            fontSize: '13px',
            color: 'text.secondary',
            display: 'flex',
            gap: 0.75,
          }}
        >
          <MuiLink
            component={RouterLink}
            to="/parts/search"
            underline="none"
            sx={{ color: 'v4.accent' }}
          >
            Parts
          </MuiLink>
          <span>/</span>
          <span>{part.serial1}</span>
        </Box>

        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 50,
            bgcolor: 'background.default',
            px: 2.5,
            py: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <PartSummaryBar
            serial1={part.serial1}
            partTypeId={part.partTypeId}
            partTypeName={partTypeName}
            partNumber={partNumber}
            conditionName={part.conditionName}
            locationHierarchy={locationHierarchy}
            tags={part.tags}
            hasDuplicateSerial={duplicateCount > 0}
            saving={saving}
            canEdit={canEdit}
            isDirty={isDirty}
            onSave={handleSave}
            onDiscard={canEdit ? handleDiscard : undefined}
            rightSlot={
              <>
                <PrintLabel entityId={part.id} entityType={PrintLabelEntityTypes.Part} />
                <CreateRepairJobButton partId={part.id} serial={part.serial1} />
              </>
            }
          />
        </Box>

        <Box sx={{ px: 2.5, pt: 1.75 }}>
          <MainPartEditFragment
            ref={editRef}
            data={part}
            conditions={conditions}
            duplicatesSlot={duplicatesSlot}
            onSaved={handleSaved}
            onDirtyChange={handleDirtyChange}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.75,
            px: 2.5,
            pt: 1.75,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.75,
              minWidth: 0,
            }}
          >
            <LinkedJobsPanel partHistory={partHistory} sx={{ flex: 1 }} />
            <LinkedShipmentsPanel partHistory={partHistory} sx={{ flex: 1 }} />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0 }}>
            <Panel title="Warranty">
              <WarrantySummaryFragment
                warrantySummarydata={warrantySummary}
                firstInstalled={part.firstInstalled}
              />
            </Panel>
            {part.partChildrens && part.partChildrens.length > 0 && (
              <Panel title="Child Parts">
                <PartChildrenList partChildrenListData={part.partChildrens} />
              </Panel>
            )}
          </Box>
        </Box>

        <Box sx={{ px: 2.5, pt: 1.75 }}>
          <PartHistoryFragment partId={partId} />
        </Box>
      </Box>
    </PageContainer>
  );
};
